/**
 * /how-it-works/formula — the Social Capital scoring formula.
 *
 * Turns the abstract multiplication into a visual, reader-friendly
 * explainer. Anyone landing here should walk away understanding why
 * the same act (say, planting a tree) can score very differently
 * depending on how it's evidenced, where it happens, and how big
 * the impact is.
 */
import { Link } from "react-router-dom";
import {
  Calculator, ShieldCheck, TrendingUp, Sparkles, Users, Star, Award,
  ArrowRight, X as Xicon,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";

const FACTORS = [
  {
    key: "base",   Icon: Calculator, tint: "#0F172A",
    name: "Base Value",
    tagline: "The baseline points for that kind of action.",
    body: "Every eligible activity is anchored to a base value — mentoring an hour is worth different points than planting a tree, which is different from rescuing a meal. Base values live in a public, governed table so they're consistent across the network.",
    example: "Planting one tree = 10 base points.",
  },
  {
    key: "verify", Icon: ShieldCheck, tint: "#059669",
    name: "Verification",
    tagline: "Who confirmed it happened.",
    body: "Self-declared actions weigh 1×. A community peer or organiser confirming it lifts the weight to about 1.5×. A verified nonprofit, school or municipality vouching lifts it to 2×. System-verified (e.g. a nature observation cross-checked against a public dataset) can go higher still.",
    example: "The tree was logged by a park ranger's org account → ×2.",
  },
  {
    key: "impact", Icon: TrendingUp, tint: "#0891B2",
    name: "Impact",
    tagline: "How many people, animals, or how much waste changed.",
    body: "A meal that fed one neighbour vs. a food-rescue haul that fed 200 don't count the same. Impact is measured in the natural units of the action (people, meals, kilos, hours, hectares) and mapped to a multiplier band.",
    example: "That tree is one of a 300-tree community planting → ×1.8.",
  },
  {
    key: "quality", Icon: Star, tint: "#D97706",
    name: "Quality",
    tagline: "How well-documented and credible the entry is.",
    body: "Photos, before/after, links, third-party mentions — all raise the quality multiplier. Not because photos matter more than deeds, but because good documentation is what lets a stranger trust the record decades later.",
    example: "Photo + coordinates + community-event link → ×1.3.",
  },
  {
    key: "trust", Icon: Users, tint: "#7C3AED",
    name: "Trust",
    tagline: "Your own reputation on the network.",
    body: "As your verified contributions accumulate, your trust score rises. A trusted contributor's new entries carry slightly more weight — a small, steady bias in favour of people who've built a track record.",
    example: "Contributor with 250+ verified actions → ×1.1.",
  },
  {
    key: "rarity", Icon: Award, tint: "#DC2626",
    name: "Rarity",
    tagline: "Unusual, hard-to-fake or exceptionally impactful actions.",
    body: "A blood donation streak of 25 years, restoring a nearly-extinct species' habitat, or documenting a dying language — these are rare enough that we recognise them with a rarity boost. Rarity is human-curated and cross-checked to avoid abuse.",
    example: "First documented sighting of a species in a district → ×1.5.",
  },
];

const SAMPLE = { base: 10, verify: 2.0, impact: 1.8, quality: 1.3, trust: 1.1, rarity: 1.0 };
const SAMPLE_TOTAL = Math.round(SAMPLE.base * SAMPLE.verify * SAMPLE.impact * SAMPLE.quality * SAMPLE.trust * SAMPLE.rarity);

export default function HowItWorksFormulaPage() {
  return (
    <div className="max-w-5xl mx-auto px-6 py-10 md:py-14" data-testid="sc-formula-page">
      <SeoHead section="social-capital"
               title="The Social Capital Formula · SocialCapital.cc"
               description="Base × Verification × Impact × Quality × Trust × Rarity = Social Capital Points. Here's what each factor means."
               url="/how-it-works/formula" />

      {/* Hero */}
      <header className="mb-10">
        <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: "#059669" }}>
          How It Works · The formula
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold" style={{ color: "#0f172a" }}>
          One formula. Six factors.
        </h1>
        <p className="mt-4 text-base sm:text-lg opacity-80 max-w-2xl">
          Every action's score comes from the same multiplication. Nothing
          hidden, nothing arbitrary — anyone can read this page and reproduce
          any score.
        </p>
      </header>

      {/* Formula ribbon */}
      <section
        className="rounded-3xl p-6 sm:p-8 mb-10 border"
        style={{
          background: "linear-gradient(135deg,#065F46 0%,#059669 60%,#10B981 100%)",
          color: "#F0FDF4",
          borderColor: "transparent",
        }}
        data-testid="sc-formula-ribbon"
      >
        <div className="text-white/70 text-xs font-mono uppercase tracking-widest mb-3">
          Social Capital Points =
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 font-display text-lg sm:text-2xl">
          {["Base Value", "Verification", "Impact", "Quality", "Trust", "Rarity"].map((f, i, a) => (
            <span key={f} className="inline-flex items-center gap-2">
              <span className="rounded-full px-3 py-1"
                    style={{ background: "rgba(255,255,255,0.14)" }}>
                {f}
              </span>
              {i < a.length - 1 && <Xicon className="w-4 h-4 opacity-70" aria-hidden />}
            </span>
          ))}
        </div>
      </section>

      {/* Factor cards */}
      <section className="grid md:grid-cols-2 gap-4 mb-12" data-testid="sc-formula-factors">
        {FACTORS.map(({ key, Icon, tint, name, tagline, body, example }) => (
          <article key={key}
                   className="rounded-2xl border p-6 bg-white"
                   style={{ borderColor: "rgba(0,0,0,0.06)" }}
                   data-testid={`sc-factor-${key}`}>
            <Icon className="w-6 h-6" style={{ color: tint }} />
            <h3 className="font-display text-xl mt-3">{name}</h3>
            <p className="text-sm font-medium opacity-80 mt-1">{tagline}</p>
            <p className="text-sm opacity-75 mt-3 leading-relaxed">{body}</p>
            <div className="mt-4 rounded-lg px-3 py-2 text-xs font-mono"
                 style={{ background: "#F8FAFC", color: "#0F172A" }}>
              <span className="opacity-60">Example · </span>{example}
            </div>
          </article>
        ))}
      </section>

      {/* Worked example */}
      <section
        className="rounded-2xl border p-6 sm:p-8 mb-12"
        style={{ borderColor: "rgba(0,0,0,0.08)", background: "#F9FAFB" }}
        data-testid="sc-formula-example"
      >
        <div className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: "#059669" }}>
          Worked example — a community tree planting
        </div>
        <h3 className="font-display text-2xl mb-4">You planted a tree, verified by a park ranger.</h3>
        <div className="flex flex-wrap items-center gap-2 text-base sm:text-lg font-display mb-5">
          <span className="tabular-nums">{SAMPLE.base}</span>
          <Xicon className="w-4 h-4 opacity-50" />
          <span className="tabular-nums">{SAMPLE.verify}</span>
          <Xicon className="w-4 h-4 opacity-50" />
          <span className="tabular-nums">{SAMPLE.impact}</span>
          <Xicon className="w-4 h-4 opacity-50" />
          <span className="tabular-nums">{SAMPLE.quality}</span>
          <Xicon className="w-4 h-4 opacity-50" />
          <span className="tabular-nums">{SAMPLE.trust}</span>
          <Xicon className="w-4 h-4 opacity-50" />
          <span className="tabular-nums">{SAMPLE.rarity}</span>
          <span className="mx-2 opacity-60">=</span>
          <span className="text-3xl sm:text-4xl font-bold" style={{ color: "#059669" }}>
            {SAMPLE_TOTAL} pts
          </span>
        </div>
        <p className="text-sm opacity-75 leading-relaxed max-w-3xl">
          Same act on a lazy day (no photos, no group event, no ranger
          confirmation) would score closer to <b>10 × 1 × 1 × 1 × 1 × 1 = 10 pts</b>.
          Same act as the first documented sighting of a rare tree species
          could easily be worth 3–4× more. Documentation, community context
          and rarity are what earn multipliers.
        </p>
      </section>

      {/* CTA row */}
      <section className="grid sm:grid-cols-2 gap-4">
        <Link to="/how-it-works/point-values"
              className="rounded-2xl border p-6 hover:border-emerald-300 transition-colors bg-white"
              style={{ borderColor: "rgba(0,0,0,0.08)" }}
              data-testid="sc-formula-cta-points">
          <div className="overline mb-2" style={{ color: "#059669" }}>See the numbers</div>
          <div className="font-display text-xl mb-2">Point Values &amp; Categories</div>
          <p className="text-sm opacity-70 leading-relaxed">
            The public table of base points, verification rules and possible
            multipliers for every eligible activity.
          </p>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold"
               style={{ color: "#059669" }}>
            Open the table <ArrowRight className="w-4 h-4" />
          </div>
        </Link>

        <Link to="/earn/submit"
              className="rounded-2xl border p-6 hover:border-emerald-300 transition-colors bg-white"
              style={{ borderColor: "rgba(0,0,0,0.08)" }}
              data-testid="sc-formula-cta-submit">
          <div className="overline mb-2" style={{ color: "#059669" }}>Try it now</div>
          <div className="font-display text-xl mb-2">Submit a real action</div>
          <p className="text-sm opacity-70 leading-relaxed">
            Log something you already did — the reviewer's approval turns
            the formula into your first passport entry.
          </p>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold"
               style={{ color: "#059669" }}>
            Log a contribution <Sparkles className="w-4 h-4" />
          </div>
        </Link>
      </section>
    </div>
  );
}
